import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KINDS, dueLabel } from "@/components/documents/docConst";

const EMPTY = { kind: "contratto", title: "", company_id: null, counterparty: "", number: "", issue_date: null, due_date: null, owner_user_id: null, notes: "" };
const GROUP = "__group__";
const NONE = "__none__";

const F = ({ label, children, wide }) => <div className={wide ? "sm:col-span-2" : ""}><Label className="micro-label">{label}</Label><div className="mt-1.5">{children}</div></div>;

const Pick = ({ value, onChange, options, testId, none }) => (
  <Select value={value ?? none} onValueChange={(v) => onChange(v === none ? null : v)}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="Seleziona" /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const DocumentDialog = ({ doc, owners, onClose, onSaved }) => {
  const { user } = useAuth();
  const { visibleCompanies: companies } = useData();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const groupOk = user.role === "admin" || user.group_scope;
  useEffect(() => { if (doc) { setForm(doc === "new" ? { ...EMPTY, company_id: groupOk ? null : companies[0]?.id ?? null } : { ...EMPTY, ...doc }); setFile(null); } }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const companyOpts = [...(groupOk ? [[GROUP, "Tutto il gruppo"]] : []), ...companies.map((c) => [c.id, c.legal_name])];

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]]));
      const r = doc === "new" ? await api.post("/documents", body) : await api.put(`/documents/${doc.id}`, body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/documents/${r.data.id}/versions`, fd); } catch (e) { toast.error(`Documento salvato, ma il file non è stato caricato: ${errMsg(e)}. Usa "Rinnova" per caricarlo.`); onSaved(); return; }
      }
      toast.success("Documento salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="document-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{doc === "new" ? "Nuovo documento" : "Modifica documento"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Tipo"><Pick value={form.kind} onChange={set("kind")} options={Object.entries(KINDS)} testId="document-field-kind" /></F>
          <F label="Società"><Pick value={form.company_id} none={GROUP} onChange={set("company_id")} options={companyOpts} testId="document-field-company" /></F>
          <F label="Titolo" wide><Input value={form.title} onChange={(e) => set("title")(e.target.value)} data-testid="document-field-title" placeholder="Es. Contratto quadro FiberCop 2026" /></F>
          <F label={form.kind === "certificazione" ? "Ente certificatore" : form.kind === "procedura" ? "Area / ambito" : "Controparte"}><Input value={form.counterparty} onChange={(e) => set("counterparty")(e.target.value)} data-testid="document-field-counterparty" /></F>
          <F label="Numero / versione"><Input value={form.number} onChange={(e) => set("number")(e.target.value)} data-testid="document-field-number" /></F>
          <F label="Data emissione"><DatePicker value={form.issue_date || ""} onChange={(v) => set("issue_date")(v || null)} clearable testId="document-field-issue" /></F>
          <F label={`${dueLabel(form.kind)}${form.kind === "procedura" ? " (facoltativa)" : ""}`}><DatePicker value={form.due_date || ""} onChange={(v) => set("due_date")(v || null)} clearable testId="document-field-due" /></F>
          <F label="Responsabile (riceve gli avvisi)" wide><Pick value={form.owner_user_id} none={NONE} onChange={set("owner_user_id")} options={[[NONE, "— Solo amministratori —"], ...owners.map((u) => [u.id, `${u.name} · ${u.email}`])]} testId="document-field-owner" /></F>
          <F label="Note" wide><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="document-field-notes" /></F>
          {doc === "new" && <F label="File (PDF, immagine, Word/Excel — max 15MB)" wide><Input type="file" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="document-field-file" /></F>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !form.title.trim()} data-testid="document-save-button">{busy ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
